import { generateHTML, generateJSON } from "@tiptap/html"
import { Node, Slice } from "@tiptap/pm/model"
import { Mapping, Step, Transform } from "@tiptap/pm/transform"

import type { Change } from "./diff-docs"
import type { JSONContent } from "@tiptap/core"
import type { Schema } from "@tiptap/pm/model"
import {
	getProposalEditorExtensions,
	getProposalEditorSchema,
} from "../tiptap/proposal-schema"
import { diffDocs } from "./diff-docs"
import { recreateTransformBlockwise } from "./recreate-blockwise"

export interface ProposalStepConflict {
	stepIndex: number
	description: string
	// Carried so async resolvers (`repairConflicts`, AI rebase) can rebuild the
	// failing Step instead of inferring it from `stepIndex`. Absent only when
	// JSON parsing itself failed.
	originalStepJson?: unknown
}

export interface RebaseResult {
	appliedHtml: string
	rebasedSteps: unknown[]
	conflicts: ProposalStepConflict[]
	usedChain: boolean
}

interface ResolverContext {
	step: Step
	baseDoc: Node
	currentDoc: Node
	schema: Schema
}

/**
 * Plug-in for the rebase loop's "this step couldn't be mapped" fallback.
 * Default is `contentAnchoredResolver`. AI rebasing replaces this with an
 * LLM-backed implementation. Must be deterministic and synchronous — async
 * resolvers go through `repairConflicts()` as a second phase.
 *
 * `baseDoc` is the doc the failing step's positions are valid against
 * (= base + earlier proposal steps applied), not necessarily the proposal's
 * true base.
 */
export interface ConflictResolver {
	resolve(ctx: ResolverContext): Step | null
}

export interface AsyncConflictResolver {
	resolve(ctx: ResolverContext): Promise<Step | null>
}

// `simplifyDiff: false` keeps each contiguous change as its own step. The
// wider replaces from `simplifyDiff: true` make rebases of unrelated edits
// look overlapping to ProseMirror's mapping (positions inside a "deleted"
// range get dropped), so we trade larger step lists for cleaner OT rebases.
const RECREATE_OPTS = {
	complexSteps: true,
	wordDiffs: false,
	simplifyDiff: false,
} as const

function htmlToDoc(html: string, schema: Schema): Node {
	return Node.fromJSON(
		schema,
		generateJSON(html || "<p></p>", getProposalEditorExtensions()),
	)
}

function docToHtml(doc: Node): string {
	return generateHTML(
		doc.toJSON() as JSONContent,
		getProposalEditorExtensions(),
	)
}

function synthesizeSteps(fromDoc: Node, toDoc: Node): Step[] {
	return recreateTransformBlockwise(fromDoc, toDoc, RECREATE_OPTS).steps
}

const LONE_SURROGATE =
	/\\u(?:d[89ab][0-9a-f]{2}(?!\\ud[c-f])|d[c-f][0-9a-f]{2})|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/i

// Compute steps that take baseHtml → proposedHtml. Run at proposal write time
// so the proposal stores the *intent* rather than a final snapshot.
//
// recreateTransform can emit a ReplaceStep whose slice open depths don't match
// its cut positions for large cross-block rewrites (e.g. agent markdown→HTML vs
// the existing article), producing steps that throw "Inconsistent open depths"
// on replay. Validate against base; on failure fall back to one full-document
// replace whose open depths PM computes correctly, so the proposal always
// applies (the displayed hunks stay fine-grained - see computeProposalHunks).
export function computeProposalSteps(
	baseHtml: string,
	proposedHtml: string,
): unknown[] {
	const schema = getProposalEditorSchema()
	const baseDoc = htmlToDoc(baseHtml, schema)
	const proposedDoc = htmlToDoc(proposedHtml, schema)
	// Validate the serialized steps through the exact path callers replay them
	// (`applyProposalSteps`): recreateTransform can emit steps valid in memory
	// but invalid once round-tripped through JSON, so checking the in-memory
	// Step objects isn't enough. The diff itself is inside the guard because
	// recreateTransform throws outright on some documents, and a proposal that
	// cannot be diffed still has to produce a usable full replace.
	try {
		const stepsJson = synthesizeSteps(baseDoc, proposedDoc).map(
			(step) => step.toJSON() as unknown,
		)
		applyProposalSteps(baseHtml, stepsJson)
		// The diff runs on UTF-16 units, so 📱 to 📧 keeps their shared high
		// surrogate and stores the low one alone, which Postgres json rejects.
		if (LONE_SURROGATE.test(JSON.stringify(stepsJson))) {
			throw new Error("split surrogate pair")
		}
		return stepsJson
	} catch {
		const fallback = new Transform(baseDoc)
		fallback.replace(
			0,
			baseDoc.content.size,
			new Slice(proposedDoc.content, 0, 0),
		)
		return fallback.steps.map((step) => step.toJSON() as unknown)
	}
}

// Apply proposal steps to a base HTML, producing the proposed doc + HTML.
export function applyProposalSteps(
	baseHtml: string,
	stepsJson: unknown[],
): { doc: Node; html: string } {
	const schema = getProposalEditorSchema()
	const transform = new Transform(htmlToDoc(baseHtml, schema))
	for (const json of stepsJson) {
		const step = Step.fromJSON(schema, json as Record<string, unknown>)
		const result = transform.maybeStep(step)
		if (result.failed) {
			throw new Error(`applyProposalSteps: step failed (${result.failed})`)
		}
	}
	return { doc: transform.doc, html: docToHtml(transform.doc) }
}

// One canonical rebase entry point. Every caller — UI preview, accept mutator,
// group-accept mutator, agent persist — routes through this. When `chainSteps`
// is provided (the article's version chain has logged appliedSteps for every
// hop), the OT rebase uses those real steps. Otherwise (or if they fail to
// apply cleanly) it synthesizes a base→current transform via recreateTransform.
//
// Algorithm (mirrors prosemirror-collab):
//   1. Build proposal Steps + inverses; drop any that don't apply against base.
//   2. Undo local, apply intervening, re-apply local mapped through later steps.
//   3. If a step still can't be mapped, hand to `resolver.resolve(...)`.
//   4. Steps that still don't apply land as conflicts. Other steps still apply —
//      one bad step doesn't poison clean edits in the same proposal.
export function rebaseProposal(args: {
	baseHtml: string
	currentHtml: string
	stepsJson: unknown[]
	chainSteps?: unknown[] | null
	resolver?: ConflictResolver
}): RebaseResult {
	const {
		baseHtml,
		currentHtml,
		stepsJson,
		chainSteps,
		resolver = contentAnchoredResolver,
	} = args

	// Fast path: nothing intervening, the proposal's steps replay cleanly.
	// `applyProposalSteps` throws on hard failure; let it propagate so the
	// mutator can mark the proposal SUPERSEDED with the error reason.
	if (baseHtml === currentHtml) {
		const { html } = applyProposalSteps(baseHtml, stepsJson)
		return {
			appliedHtml: html,
			rebasedSteps: stepsJson,
			conflicts: [],
			usedChain: chainSteps != null,
		}
	}

	const schema = getProposalEditorSchema()
	const baseDoc = htmlToDoc(baseHtml, schema)
	const { interveningSteps, usedChain } = resolveInterveningSteps({
		baseDoc,
		currentHtml,
		chainSteps,
		schema,
	})

	const result = rebaseInternal({
		baseDoc,
		interveningSteps,
		stepsJson,
		schema,
		resolver,
	})
	return {
		appliedHtml: result.resultHtml ?? currentHtml,
		rebasedSteps: result.rebasedSteps.map((step) => step.toJSON() as unknown),
		conflicts: result.conflicts,
		usedChain,
	}
}

// Pick the steps that take baseDoc → currentDoc. Prefers the logged chain when
// it applies cleanly; falls back to a synthesized snapshot transform otherwise.
//
// A partially-applied chain would lie about where positions land, silently
// corrupting subsequent proposal-step mappings. Detecting failure here and
// falling back keeps the rebase internally consistent.
function resolveInterveningSteps(args: {
	baseDoc: Node
	currentHtml: string
	chainSteps: unknown[] | null | undefined
	schema: Schema
}): { interveningSteps: Step[]; usedChain: boolean } {
	const { baseDoc, currentHtml, chainSteps, schema } = args
	const fallback = () => ({
		interveningSteps: synthesizeSteps(baseDoc, htmlToDoc(currentHtml, schema)),
		usedChain: false,
	})

	if (chainSteps == null) return fallback()

	const parsed: Step[] = []
	for (const json of chainSteps) {
		try {
			parsed.push(Step.fromJSON(schema, json as Record<string, unknown>))
		} catch {
			// Malformed hop: skip silently — surrounding hops still map.
		}
	}

	const probe = new Transform(baseDoc)
	for (const step of parsed) {
		if (safeMaybeStep(probe, step).failed) return fallback()
	}
	return { interveningSteps: parsed, usedChain: true }
}

interface RebaseInternalResult {
	rebasedSteps: Step[]
	conflicts: ProposalStepConflict[]
	resultDoc?: Node
	resultHtml?: string
	// `proposedToResult` maps proposed-doc coords to the rebased result doc;
	// `currentToResult` maps the running (pre-rebase) doc to it. Attribute a
	// proposal's hunks without a leave-one-out remerge.
	proposedToResult?: Mapping
	currentToResult?: Mapping
	proposedDoc?: Node
}

function rebaseInternal(args: {
	baseDoc: Node
	interveningSteps: Step[]
	stepsJson: unknown[]
	schema: Schema
	resolver: ConflictResolver
}): RebaseInternalResult {
	const { baseDoc, interveningSteps, stepsJson, schema, resolver } = args

	// Phase 1: parse + invert proposal steps. `asOfDocs[i]` is the doc against
	// which `localSteps[i]` was authored (= base + steps[0..i-1] applied). The
	// resolver needs this for k > 0, whose positions live in that intermediate
	// coordinate space rather than in baseDoc.
	const localSteps: Step[] = []
	const localInverses: Step[] = []
	const asOfDocs: Node[] = []
	let proposedDoc: Node = baseDoc
	const earlyConflicts: ProposalStepConflict[] = []
	for (let i = 0; i < stepsJson.length; i++) {
		const json = stepsJson[i]
		let step: Step
		try {
			step = Step.fromJSON(schema, json as Record<string, unknown>)
		} catch {
			earlyConflicts.push({
				stepIndex: i,
				description: "step could not be parsed",
				originalStepJson: json,
			})
			continue
		}
		let inv: Step
		try {
			inv = step.invert(proposedDoc)
		} catch {
			earlyConflicts.push({
				stepIndex: i,
				description: "step could not be inverted",
				originalStepJson: step.toJSON(),
			})
			continue
		}
		const stepResult = step.apply(proposedDoc)
		if (stepResult.failed || !stepResult.doc) {
			earlyConflicts.push({
				stepIndex: i,
				description: `step does not apply to base: ${stepResult.failed ?? "unknown"}`,
				originalStepJson: step.toJSON(),
			})
			continue
		}
		asOfDocs.push(proposedDoc)
		localSteps.push(step)
		localInverses.push(inv)
		proposedDoc = stepResult.doc
	}

	if (localSteps.length === 0) {
		// No rebaseable steps: materialize currentDoc by replaying intervening
		// on top of baseDoc.
		const t = new Transform(baseDoc)
		for (const s of interveningSteps) safeMaybeStep(t, s)
		return {
			rebasedSteps: [],
			conflicts: earlyConflicts,
			resultDoc: t.doc,
			resultHtml: docToHtml(t.doc),
			proposedToResult: new Mapping(),
			currentToResult: new Mapping(),
			proposedDoc: baseDoc,
		}
	}

	// Both sides diff at character level, so mapping one side's steps into a
	// paragraph the other side rewrote interleaves their letters (an English
	// suggestion over a Dutch rewrite came out as a mix of both). Each paragraph
	// therefore takes the proposal's steps for it all together or not at all, and
	// never into a paragraph that changed since the proposal's base.
	const interveningTransform = new Transform(baseDoc)
	for (const step of interveningSteps) {
		safeMaybeStep(interveningTransform, step)
	}
	const currentDoc = interveningTransform.doc
	const groups = groupStepsByTextblock({
		baseDoc,
		proposedDoc,
		localSteps,
		localInverses,
	})
	const changedBlocks = changedTextblocks(
		baseDoc,
		currentDoc,
		interveningTransform.mapping,
	)
	const skippedGroups = new Set<number>()
	for (let i = 0; i < localSteps.length; i++) {
		if (groups.baseBlocks[i]!.some((pos) => changedBlocks.has(pos))) {
			skippedGroups.add(groups.groupOf[i]!)
		}
	}

	const allowedBlocks = textblockKeys(currentDoc)
	for (const key of textblockKeys(proposedDoc)) allowedBlocks.add(key)

	const runPhase2 = (skip: Set<number>) =>
		replayLocalSteps({
			proposedDoc,
			localSteps,
			localInverses,
			interveningSteps,
			asOfDocs,
			baseDoc,
			schema,
			resolver,
			skip: (i) => skip.has(groups.groupOf[i]!),
		})

	let phase2 = runPhase2(skippedGroups)
	// A step that fails on its own leaves the rest of its paragraph half
	// applied, so its whole group goes and the replay runs again.
	for (;;) {
		const failedGroups = phase2.failedSteps
			.map((i) => groups.groupOf[i]!)
			.filter((group) => !skippedGroups.has(group))
		if (failedGroups.length === 0) break
		for (const group of failedGroups) skippedGroups.add(group)
		phase2 = runPhase2(skippedGroups)
	}
	// Last line of defence: every paragraph in the result must be verbatim from
	// the current article or the proposal. Anything else keeps the article as is.
	if (!everyTextblockAllowed(phase2.transform.doc, allowedBlocks)) {
		phase2 = runPhase2(new Set(groups.groupOf))
	}

	const { transform, rebasedStartIndex } = phase2
	return {
		rebasedSteps: transform.steps.slice(rebasedStartIndex),
		conflicts: [...earlyConflicts, ...phase2.conflicts],
		resultDoc: transform.doc,
		resultHtml: docToHtml(transform.doc),
		proposedToResult: transform.mapping,
		currentToResult: transform.mapping.slice(rebasedStartIndex),
		proposedDoc,
	}
}

// Phase 2: undo local → apply intervening → re-apply local with mapping.
// Steps for which `skip` holds are recorded as conflicts without applying.
function replayLocalSteps(args: {
	proposedDoc: Node
	localSteps: Step[]
	localInverses: Step[]
	interveningSteps: Step[]
	asOfDocs: Node[]
	baseDoc: Node
	schema: Schema
	resolver: ConflictResolver
	skip: (stepIndex: number) => boolean
}): {
	transform: Transform
	rebasedStartIndex: number
	conflicts: ProposalStepConflict[]
	failedSteps: number[]
} {
	const {
		proposedDoc,
		localSteps,
		localInverses,
		interveningSteps,
		asOfDocs,
		baseDoc,
		schema,
		resolver,
		skip,
	} = args
	const transform = new Transform(proposedDoc)
	for (let i = localInverses.length - 1; i >= 0; i--) {
		safeMaybeStep(transform, localInverses[i]!)
	}
	for (const step of interveningSteps) {
		safeMaybeStep(transform, step)
	}
	// Anything appended past this point is the proposal's effect against the
	// current doc — what we log as `appliedSteps` on the resulting version row.
	const rebasedStartIndex = transform.steps.length

	const conflicts: ProposalStepConflict[] = []
	const failedSteps: number[] = []
	// transform.mapping so far holds [local-inverses..., intervening...]. The
	// first inverse is at index 0; mapFrom starts pointing past it.
	let mapFrom = localInverses.length

	for (let i = 0; i < localSteps.length; i++) {
		const step = localSteps[i]!
		if (skip(i)) {
			mapFrom--
			conflicts.push({
				stepIndex: i,
				description: describeStepConflict(step),
				originalStepJson: step.toJSON(),
			})
			continue
		}

		let mapping
		try {
			mapping = transform.mapping.slice(mapFrom)
		} catch {
			mapping = null
		}

		let mapped: Step | null = null
		if (mapping) {
			try {
				mapped = step.map(mapping)
			} catch {
				mapped = null
			}
		}

		let applied = false
		if (mapped && !safeMaybeStep(transform, mapped).failed) {
			applied = true
		}
		// Decrement BEFORE setMirror — the inverse for step i lives at slot
		// `mapFrom - 1` once we've consumed slot `mapFrom` for this iteration.
		// Mirroring the *inverse* slot with the rebased step is what prevents
		// later steps' positions inside this proposal's own inserted content
		// from being treated as "deleted" when they map through the OT slice.
		// Calling setMirror with the pre-decrement `mapFrom` is a no-op (mirrors
		// the rebased step with itself) and the bug shows up as: any step whose
		// positions live inside an earlier step's insertion conflicts even
		// though the OT mapping should be identity through the inv ↔ rebased
		// pair. See prosemirror-collab's `rebaseSteps` for the canonical order.
		mapFrom--
		if (applied) {
			try {
				;(
					transform.mapping as unknown as {
						setMirror?: (n: number, m: number) => void
					}
				).setMirror?.(mapFrom, transform.steps.length - 1)
			} catch {
				/* optional optimization */
			}
		}

		if (!applied) {
			const repaired = resolver.resolve({
				step,
				baseDoc: asOfDocs[i] ?? baseDoc,
				currentDoc: transform.doc,
				schema,
			})
			const repairedResult = repaired
				? safeMaybeStep(transform, repaired)
				: { failed: "no resolver match" }
			if (repairedResult.failed) {
				failedSteps.push(i)
				conflicts.push({
					stepIndex: i,
					description: describeStepConflict(step),
					originalStepJson: step.toJSON(),
				})
			}
		}
	}

	return { transform, rebasedStartIndex, conflicts, failedSteps }
}

// Positions a step reads or writes in the doc it was authored against.
function stepRange(step: Step): { from: number; to: number } | null {
	const json = step.toJSON() as { from?: unknown; to?: unknown; pos?: unknown }
	if (typeof json.from === "number" && typeof json.to === "number") {
		return { from: json.from, to: json.to }
	}
	if (typeof json.pos === "number") return { from: json.pos, to: json.pos + 1 }
	return null
}

// Start positions of the textblocks a range touches, including the ones its
// endpoints sit in.
function touchedTextblocks(doc: Node, from: number, to: number): number[] {
	const size = doc.content.size
	const start = Math.max(0, Math.min(from, size))
	const end = Math.max(start, Math.min(to, size))
	const out = new Set<number>()
	const addContaining = (pos: number) => {
		const $pos = doc.resolve(pos)
		for (let depth = $pos.depth; depth > 0; depth--) {
			if ($pos.node(depth).isTextblock) {
				out.add($pos.before(depth))
				return
			}
		}
	}
	addContaining(start)
	addContaining(end)
	if (end > start) {
		doc.nodesBetween(start, end, (node, pos) => {
			if (node.isTextblock) {
				out.add(pos)
				return false
			}
			return true
		})
	}
	return [...out]
}

// Union the proposal's steps that touch the same paragraph, on either the base
// or the proposed side, so a paragraph's edits apply or skip as one.
function groupStepsByTextblock(args: {
	baseDoc: Node
	proposedDoc: Node
	localSteps: Step[]
	localInverses: Step[]
}): { groupOf: number[]; baseBlocks: number[][] } {
	const { baseDoc, proposedDoc, localSteps, localInverses } = args
	const n = localSteps.length
	const toBase = new Mapping(
		[...localInverses].reverse().map((inverse) => inverse.getMap()),
	)
	const toProposed = new Mapping(localSteps.map((step) => step.getMap()))

	const parent = Array.from({ length: n }, (_, i) => i)
	const find = (i: number): number => {
		while (parent[i] !== i) {
			parent[i] = parent[parent[i]!]!
			i = parent[i]!
		}
		return i
	}
	const ownerOfKey = new Map<string, number>()
	const join = (i: number, key: string) => {
		const owner = ownerOfKey.get(key)
		if (owner === undefined) ownerOfKey.set(key, i)
		else parent[find(i)] = find(owner)
	}

	const baseBlocks: number[][] = []
	for (let i = 0; i < n; i++) {
		const step = localSteps[i]!
		const range = stepRange(step)
		if (!range) {
			baseBlocks.push([])
			continue
		}
		const back = toBase.slice(n - i)
		const base = touchedTextblocks(
			baseDoc,
			back.map(range.from, -1),
			back.map(range.to, 1),
		)
		baseBlocks.push(base)
		for (const pos of base) join(i, `b${pos}`)

		const own = step.getMap()
		const forward = toProposed.slice(i + 1)
		const proposed = touchedTextblocks(
			proposedDoc,
			forward.map(own.map(range.from, -1), -1),
			forward.map(own.map(range.to, 1), 1),
		)
		for (const pos of proposed) join(i, `p${pos}`)
	}

	return { groupOf: parent.map((_, i) => find(i)), baseBlocks }
}

// Start positions of base textblocks that are not carried over verbatim into
// the current doc.
function changedTextblocks(
	baseDoc: Node,
	currentDoc: Node,
	mapping: Mapping,
): Set<number> {
	const changed = new Set<number>()
	baseDoc.descendants((node, pos) => {
		if (!node.isTextblock) return true
		const start = mapping.mapResult(pos, 1)
		const end = mapping.mapResult(pos + node.nodeSize, -1)
		const survived =
			!start.deleted &&
			!end.deleted &&
			end.pos - start.pos === node.nodeSize &&
			currentDoc.nodeAt(start.pos)?.eq(node) === true
		if (!survived) changed.add(pos)
		return false
	})
	return changed
}

function textblockKeys(doc: Node): Set<string> {
	const keys = new Set<string>()
	doc.descendants((node) => {
		if (!node.isTextblock) return true
		keys.add(JSON.stringify(node.toJSON()))
		return false
	})
	return keys
}

function everyTextblockAllowed(doc: Node, allowed: Set<string>): boolean {
	let ok = true
	doc.descendants((node) => {
		if (!ok) return false
		if (!node.isTextblock) return true
		if (!allowed.has(JSON.stringify(node.toJSON()))) ok = false
		return false
	})
	return ok
}

// Default `ConflictResolver`: locate the step's source slice in baseDoc by
// text content, re-find that text in currentDoc, and produce a retargeted Step.
// Returns null when retarget isn't possible (caller records a conflict).
//
// Position semantics across step types:
//   - replace:       [from, to)            — range being replaced
//   - replaceAround: [from, gapFrom) ∪ [gapTo, to) replaced; gap preserved
const ANCHOR_LEN = 24

interface RetargetJson {
	stepType?: string
	from?: number
	to?: number
	gapFrom?: number
	gapTo?: number
	insert?: number
	structure?: boolean
	slice?: unknown
}

export const contentAnchoredResolver: ConflictResolver = {
	resolve({ step, baseDoc, currentDoc, schema }) {
		try {
			const json = step.toJSON() as RetargetJson
			if (json.stepType === "replace") {
				return retargetReplace(json, baseDoc, currentDoc, schema)
			}
			if (json.stepType === "replaceAround") {
				return retargetReplaceAround(json, baseDoc, currentDoc, schema)
			}
			return null
		} catch {
			return null
		}
	},
}

function inDocRange(doc: Node, ...positions: (number | undefined)[]): boolean {
	const size = doc.content.size
	for (const p of positions) {
		if (typeof p !== "number" || p < 0 || p > size) return false
	}
	return true
}

function retargetReplace(
	json: RetargetJson,
	baseDoc: Node,
	currentDoc: Node,
	schema: Schema,
): Step | null {
	if (typeof json.from !== "number" || typeof json.to !== "number") return null
	// Step k > 0 carries positions valid against base + earlier-steps-applied,
	// which may be out of baseDoc's range. Bail rather than throwing.
	if (!inDocRange(baseDoc, json.from, json.to)) return null

	const replaced =
		json.from < json.to ? baseDoc.textBetween(json.from, json.to, " ", " ") : ""
	const before = baseDoc.textBetween(
		Math.max(0, json.from - ANCHOR_LEN),
		json.from,
		" ",
		" ",
	)
	const anchorText = (before + replaced).trim()
	if (anchorText.length === 0) return null

	const found = findTextRange(currentDoc, anchorText)
	if (!found) return null

	const newFrom = found.from + before.length
	return Step.fromJSON(schema, {
		...json,
		from: newFrom,
		to: newFrom + replaced.length,
	})
}

function retargetReplaceAround(
	json: RetargetJson,
	baseDoc: Node,
	currentDoc: Node,
	schema: Schema,
): Step | null {
	if (
		typeof json.from !== "number" ||
		typeof json.to !== "number" ||
		typeof json.gapFrom !== "number" ||
		typeof json.gapTo !== "number"
	) {
		return null
	}
	if (!inDocRange(baseDoc, json.from, json.to, json.gapFrom, json.gapTo)) {
		return null
	}

	// Anchor on the gap content (it survives the operation untouched), prefixed
	// with a small text window for disambiguation.
	const gapText =
		json.gapFrom < json.gapTo
			? baseDoc.textBetween(json.gapFrom, json.gapTo, " ", " ")
			: ""
	const beforeGap = baseDoc.textBetween(
		Math.max(0, json.gapFrom - ANCHOR_LEN),
		json.gapFrom,
		" ",
		" ",
	)
	const anchorText = (beforeGap + gapText).trim()
	if (anchorText.length === 0) return null

	const found = findTextRange(currentDoc, anchorText)
	if (!found) return null

	const delta = found.from + beforeGap.length - json.gapFrom
	return Step.fromJSON(schema, {
		...json,
		from: json.from + delta,
		to: json.to + delta,
		gapFrom: json.gapFrom + delta,
		gapTo: json.gapTo + delta,
	})
}

// PM's `transform.maybeStep` is supposed to return `{failed: ...}` for steps
// that don't apply, but for some shapes (positions out of range after mapping
// through a synthesized intervening transform) it throws. Wrap so a hard throw
// degrades to a normal failure.
function safeMaybeStep(
	transform: Transform,
	step: Step,
): { failed: string | null } {
	try {
		const result = transform.maybeStep(step)
		return { failed: result.failed ?? null }
	} catch (err) {
		return { failed: String((err as Error)?.message ?? err) }
	}
}

// Locate a text substring in a doc and return its [from, to] doc positions.
function findTextRange(
	doc: Node,
	needle: string,
): { from: number; to: number } | null {
	if (needle.length === 0) return null
	const haystack = doc.textBetween(0, doc.content.size, " ", " ")
	const idx = haystack.indexOf(needle)
	if (idx < 0) return null
	const from = textIndexToDocPos(doc, idx)
	const to = textIndexToDocPos(doc, idx + needle.length)
	if (from == null || to == null) return null
	return { from, to }
}

function textIndexToDocPos(doc: Node, targetIdx: number): number | null {
	let charsSeen = 0
	let foundPos: number | null = null
	doc.descendants((node, pos) => {
		if (foundPos != null) return false
		if (node.isText) {
			const len = node.text?.length ?? 0
			if (charsSeen + len >= targetIdx) {
				foundPos = pos + (targetIdx - charsSeen)
				return false
			}
			charsSeen += len
		} else if (node.isLeaf) {
			// Non-text leaves count as one " " in textBetween; mirror that here.
			if (charsSeen + 1 >= targetIdx) {
				foundPos = pos
				return false
			}
			charsSeen += 1
		}
		return true
	})
	return foundPos
}

function describeStepConflict(step: Step): string {
	const json = step.toJSON() as {
		stepType?: string
		from?: number
		to?: number
	}
	const range =
		typeof json.from === "number" && typeof json.to === "number"
			? ` at [${json.from}, ${json.to}]`
			: ""
	return `${json.stepType ?? "step"}${range} targets content that was changed since the proposal was drafted`
}

export interface ProposalDiffStats {
	added: number
	removed: number
}

// Top-level node types whose children should each count as a separate "line"
// for stats purposes (each list item is one line, not the whole list).
const LIST_TYPES = new Set(["bulletList", "orderedList", "taskList"])

// Flatten a doc into one entry per "line" — each top-level block, with lists
// expanded so each item is its own line. Keyed by `type:text` so that turning
// a paragraph into a heading with the same text still counts as +1/-1.
function flattenToLines(doc: Node): string[] {
	const lines: string[] = []
	doc.forEach((child) => {
		if (LIST_TYPES.has(child.type.name)) {
			child.forEach((item) => {
				lines.push(`${item.type.name}:${item.textContent}`)
			})
		} else {
			lines.push(`${child.type.name}:${child.textContent}`)
		}
	})
	return lines
}

// LCS-based line counter. Reordering counts as +1/-1 (same as `git diff`).
function countLineDiff(
	a: string[],
	b: string[],
): { added: number; removed: number } {
	const n = a.length
	const m = b.length
	const dp: number[][] = Array.from({ length: n + 1 }, () =>
		new Array<number>(m + 1).fill(0),
	)
	for (let i = 0; i < n; i++) {
		for (let j = 0; j < m; j++) {
			dp[i + 1]![j + 1] =
				a[i] === b[j]
					? dp[i]![j]! + 1
					: Math.max(dp[i]![j + 1]!, dp[i + 1]![j]!)
		}
	}
	const lcs = dp[n]![m]!
	return { added: m - lcs, removed: n - lcs }
}

// "+12 -3" badge stat for the suggestions list. Counts whole lines (top-level
// blocks + individual list items) so a one-word edit reads as +1/-1, not as a
// large character count that drowns out the rest of the rail.
export function computeProposalDiffStats(
	baseHtml: string,
	proposedHtml: string,
): ProposalDiffStats {
	const schema = getProposalEditorSchema()
	const baseLines = flattenToLines(htmlToDoc(baseHtml, schema))
	const proposedLines = flattenToLines(htmlToDoc(proposedHtml, schema))
	return countLineDiff(baseLines, proposedLines)
}

export interface MergeProposalInput {
	id: string
	baseHtml: string
	stepsJson: unknown[]
}

export interface MergeProposalOutcome {
	id: string
	applied: boolean
	conflicts: ProposalStepConflict[]
}

export interface MergeProposalsResult {
	resultHtml: string
	outcomes: MergeProposalOutcome[]
}

// Apply proposals sequentially onto `currentHtml`, rebasing each against the
// running result — matches what would happen if a reviewer accepted them one
// at a time. A proposal whose steps don't rebase cleanly is skipped; later
// proposals still rebase, so one bad apple doesn't poison its siblings.
//
// Deterministic given the same input array. Callers should pre-sort on a
// stable key (e.g. createdAt then id) so re-mounts don't see merge flicker.
export function mergeProposalsForArticle(
	currentHtml: string,
	proposals: MergeProposalInput[],
): MergeProposalsResult {
	let runningHtml = currentHtml
	const outcomes: MergeProposalOutcome[] = []

	for (const proposal of proposals) {
		if (proposal.stepsJson.length === 0) {
			outcomes.push({ id: proposal.id, applied: true, conflicts: [] })
			continue
		}

		try {
			const result = rebaseProposal({
				baseHtml: proposal.baseHtml,
				currentHtml: runningHtml,
				stepsJson: proposal.stepsJson,
			})
			// rebaseProposal applies as many steps as it can even when some
			// conflict — preserve partial success in the running HTML.
			runningHtml = result.appliedHtml
			outcomes.push({
				id: proposal.id,
				applied: result.conflicts.length === 0,
				conflicts: result.conflicts,
			})
		} catch {
			outcomes.push({
				id: proposal.id,
				applied: false,
				conflicts: [
					{ stepIndex: -1, description: "could not parse proposal steps" },
				],
			})
		}
	}

	return { resultHtml: runningHtml, outcomes }
}

export interface FoldProposalInput {
	id: string
	baseHtml: string
	stepsJson: unknown[]
}

export interface ProposalFold {
	carrierId: string
	supersededIds: string[]
	combinedHtml: string
}

// Injected AI merge (kept out of the client bundle, same pattern as
// `AsyncConflictResolver`). Folds one edit (baseHtml -> proposedHtml) onto
// `headHtml`, returning the combined article HTML, or null when it can't.
export interface AsyncArticleMerger {
	merge(args: {
		headHtml: string
		baseHtml: string
		proposedHtml: string
	}): Promise<string | null>
}

export interface FoldOptions {
	// Stack cleanly-rebasing proposals into the same cluster too. One article
	// then carries one suggestion instead of one per agent run.
	combineDisjoint?: boolean
}

// Group OPEN proposals that overlap (their steps conflict when stacked) and fold
// each multi-member group into one combined article via `merger` (AI). Disjoint
// proposals already merge cleanly, so they get no fold entry and stay separate
// unless `combineDisjoint` is set, which stacks them deterministically with no
// AI call. `proposals` MUST be ordered oldest -> newest; the newest member of a
// group becomes the carrier and the rest are superseded. The caller applies the
// plan: rewrite the carrier's steps to `headHtml -> combinedHtml` and supersede
// the `supersededIds`.
export async function planProposalFold(
	headHtml: string,
	proposals: FoldProposalInput[],
	merger: AsyncArticleMerger,
	options: FoldOptions = {},
): Promise<ProposalFold[]> {
	interface Cluster {
		result: string
		members: FoldProposalInput[]
	}
	const clusters: Cluster[] = []

	for (const proposal of proposals) {
		if (proposal.stepsJson.length === 0) continue

		let placed = false
		for (const cluster of clusters) {
			let rebase: RebaseResult
			try {
				rebase = rebaseProposal({
					baseHtml: proposal.baseHtml,
					currentHtml: cluster.result,
					stepsJson: proposal.stepsJson,
				})
			} catch {
				continue
			}
			if (rebase.conflicts.length === 0) {
				if (!options.combineDisjoint) continue
				// Stacks cleanly, so the combined doc is deterministic.
				cluster.result = rebase.appliedHtml
				cluster.members.push(proposal)
				placed = true
				break
			}

			// Overlaps this cluster - AI-merge the proposal's full proposed doc
			// onto the running cluster result.
			let proposedHtml: string
			try {
				proposedHtml = applyProposalSteps(
					proposal.baseHtml,
					proposal.stepsJson,
				).html
			} catch {
				continue
			}
			const merged = await merger.merge({
				headHtml: cluster.result,
				baseHtml: proposal.baseHtml,
				proposedHtml,
			})
			// Absorbing a member the merge could not fold would supersede it and
			// drop its change from the article.
			if (!merged) continue
			cluster.result = merged
			cluster.members.push(proposal)
			placed = true
			break
		}
		if (placed) continue

		let result = headHtml
		try {
			result = rebaseProposal({
				baseHtml: proposal.baseHtml,
				currentHtml: headHtml,
				stepsJson: proposal.stepsJson,
			}).appliedHtml
		} catch {
			result = headHtml
		}
		clusters.push({ result, members: [proposal] })
	}

	return clusters
		.filter((cluster) => cluster.members.length > 1)
		.map((cluster) => ({
			carrierId: cluster.members[cluster.members.length - 1]!.id,
			supersededIds: cluster.members.slice(0, -1).map((m) => m.id),
			combinedHtml: cluster.result,
		}))
}

// Per-proposal range in the merged final doc, used to attribute a diff in the
// review editor back to the proposal that produced it. Positions are in the
// final merged doc's coordinate space — directly usable as decoration anchors.
export type ProposalDiffRange =
	| { kind: "insert"; from: number; to: number }
	| { kind: "delete"; pos: number; oldText: string }
	| { kind: "replace"; from: number; to: number; oldText: string }

// Attribute every diff range in the merged final doc to the proposal that
// produced it, via leave-one-out diffs: for each proposal P, merging
// (proposals \ P) gives the doc that would exist *without* P; diffing that
// against the full merge yields exactly P's contribution, in the full
// merge's coordinate space.
//
// Cost: N + 1 merges + diffs. N is small in practice (<10 per article); the
// caller is expected to memoize on (currentHtml, proposals).
export function computeProposalContributions(
	currentHtml: string,
	proposals: MergeProposalInput[],
): Map<string, ProposalDiffRange[]> {
	const result = new Map<string, ProposalDiffRange[]>()
	if (proposals.length === 0) return result

	const schema = getProposalEditorSchema()
	const fullHtml = mergeProposalsForArticle(currentHtml, proposals).resultHtml
	const fullDoc = htmlToDoc(fullHtml, schema)
	const docSize = fullDoc.content.size
	const clamp = (pos: number) => Math.max(0, Math.min(pos, docSize))

	for (let i = 0; i < proposals.length; i++) {
		const proposal = proposals[i]
		if (!proposal) continue

		const rest = proposals.filter((_, j) => j !== i)
		const withoutHtml =
			rest.length === 0
				? currentHtml
				: mergeProposalsForArticle(currentHtml, rest).resultHtml
		const withoutDoc = htmlToDoc(withoutHtml, schema)

		let changes
		try {
			changes = diffDocs(withoutDoc, fullDoc)
		} catch {
			result.set(proposal.id, [])
			continue
		}

		const ranges: ProposalDiffRange[] = []
		for (const change of changes) {
			if (change.type === "insert") {
				const from = clamp(change.rangeB.from)
				const to = clamp(change.rangeB.to)
				if (to > from) ranges.push({ kind: "insert", from, to })
			} else if (change.type === "delete") {
				const oldText = withoutDoc.textBetween(
					change.rangeA.from,
					change.rangeA.to,
					" ",
					" ",
				)
				if (oldText.trim().length > 0) {
					ranges.push({
						kind: "delete",
						pos: clamp(change.rangeB.pos),
						oldText,
					})
				}
			} else {
				const from = clamp(change.rangeB.from)
				const to = clamp(change.rangeB.to)
				const oldText = withoutDoc.textBetween(
					change.rangeA.from,
					change.rangeA.to,
					" ",
					" ",
				)
				ranges.push({
					kind: "replace",
					from,
					to,
					oldText: oldText.trim().length > 0 ? oldText : "",
				})
			}
		}
		result.set(proposal.id, ranges)
	}
	return result
}

function changeToDiffRange(
	change: Change,
	fromDoc: Node,
	clamp: (pos: number) => number,
): ProposalDiffRange | null {
	if (change.type === "insert") {
		const from = clamp(change.rangeB.from)
		const to = clamp(change.rangeB.to)
		return to > from ? { kind: "insert", from, to } : null
	}
	if (change.type === "delete") {
		const oldText = fromDoc.textBetween(
			change.rangeA.from,
			change.rangeA.to,
			" ",
			" ",
		)
		return oldText.trim().length > 0
			? { kind: "delete", pos: clamp(change.rangeB.pos), oldText }
			: null
	}
	const from = clamp(change.rangeB.from)
	const to = clamp(change.rangeB.to)
	const oldText = fromDoc.textBetween(
		change.rangeA.from,
		change.rangeA.to,
		" ",
		" ",
	)
	return {
		kind: "replace",
		from,
		to,
		oldText: oldText.trim().length > 0 ? oldText : "",
	}
}

// One contiguous change a single proposal makes to its base. `id` is positional
// (`${proposalId}#${index}`) and stable for a fixed (baseHtml, stepsJson), so it
// only changes when the proposal itself is re-drafted.
export interface ProposalHunk {
	id: string
	index: number
	kind: "insert" | "delete" | "replace"
	// positions in the proposed-doc coordinate space (base + all this proposal's steps)
	from: number
	to: number
	oldText: string
}

// Base range [fromA, toA) and proposed range [fromB, toB) for one Change.
function changeSpans(change: Change): {
	fromA: number
	toA: number
	fromB: number
	toB: number
} {
	if (change.type === "insert") {
		return {
			fromA: change.rangeA.pos,
			toA: change.rangeA.pos,
			fromB: change.rangeB.from,
			toB: change.rangeB.to,
		}
	}
	if (change.type === "delete") {
		return {
			fromA: change.rangeA.from,
			toA: change.rangeA.to,
			fromB: change.rangeB.pos,
			toB: change.rangeB.pos,
		}
	}
	return {
		fromA: change.rangeA.from,
		toA: change.rangeA.to,
		fromB: change.rangeB.from,
		toB: change.rangeB.to,
	}
}

// diffDocs defaults: word-level changes, adjacent ones coalesced, so each hunk
// is a readable region a reviewer can skip - not a string of one-character
// fragments. The retained subset is re-synthesized through `computeProposalSteps`
// (RECREATE_OPTS) afterwards, so coarse splitting here doesn't affect the steps
// we ultimately apply.
const HUNK_DIFF_OPTS = {
	simplifyDiff: true,
	wordDiffs: true,
} as const

// Canonical hunks for ONE proposal in isolation (base -> base + all its steps).
// Deterministic: same (proposalId, baseHtml, stepsJson) always yields the same
// ids, so client and server agree on what a discarded hunk refers to.
export function computeProposalHunks(
	proposalId: string,
	baseHtml: string,
	stepsJson: unknown[],
): ProposalHunk[] {
	const schema = getProposalEditorSchema()
	const baseDoc = htmlToDoc(baseHtml, schema)
	let proposedDoc: Node
	try {
		proposedDoc = htmlToDoc(
			applyProposalSteps(baseHtml, stepsJson).html,
			schema,
		)
	} catch {
		return []
	}
	let changes: Change[]
	try {
		changes = diffDocs(baseDoc, proposedDoc, HUNK_DIFF_OPTS)
	} catch {
		return []
	}
	return changes.map((change, index) => {
		const spans = changeSpans(change)
		const oldText =
			change.type === "insert"
				? ""
				: baseDoc.textBetween(spans.fromA, spans.toA, " ", " ")
		return {
			id: `${proposalId}#${index}`,
			index,
			kind: change.type,
			from: spans.fromB,
			to: spans.toB,
			oldText,
		}
	})
}

// Re-synthesize a proposal's steps keeping only its non-discarded hunks. Returns
// the input unchanged when nothing for this proposal is discarded (fast path
// preserves the merge memo's byte-identity), or [] when everything is discarded.
export function stepsForRetainedHunks(
	proposalId: string,
	baseHtml: string,
	stepsJson: unknown[],
	discardedHunkIds: ReadonlySet<string>,
): unknown[] {
	const prefix = `${proposalId}#`
	let anyForThisProposal = false
	for (const id of discardedHunkIds) {
		if (id.startsWith(prefix)) {
			anyForThisProposal = true
			break
		}
	}
	if (!anyForThisProposal) return stepsJson

	const schema = getProposalEditorSchema()
	const baseDoc = htmlToDoc(baseHtml, schema)
	let proposedDoc: Node
	try {
		proposedDoc = htmlToDoc(
			applyProposalSteps(baseHtml, stepsJson).html,
			schema,
		)
	} catch (err) {
		// Returning the full steps means a requested skip is not honored; the
		// caller (accept mutator) detects the unchanged result and fails safe.
		console.warn(
			`stepsForRetainedHunks: proposed doc failed for ${prefix}`,
			err,
		)
		return stepsJson
	}
	let changes: Change[]
	try {
		changes = diffDocs(baseDoc, proposedDoc, HUNK_DIFF_OPTS)
	} catch (err) {
		console.warn(`stepsForRetainedHunks: diff failed for ${prefix}`, err)
		return stepsJson
	}
	if (changes.length === 0) return stepsJson

	const retainedCount = changes.filter(
		(_, index) => !discardedHunkIds.has(`${prefix}${index}`),
	).length
	if (retainedCount === changes.length) return stepsJson
	if (retainedCount === 0) return []

	// Apply retained changes onto base from the end of the doc backwards so an
	// earlier change never invalidates a later change's base positions.
	try {
		const transform = new Transform(baseDoc)
		for (let index = changes.length - 1; index >= 0; index--) {
			if (discardedHunkIds.has(`${prefix}${index}`)) continue
			const spans = changeSpans(changes[index]!)
			transform.replace(
				spans.fromA,
				spans.toA,
				proposedDoc.slice(spans.fromB, spans.toB),
			)
		}
		return computeProposalSteps(baseHtml, docToHtml(transform.doc))
	} catch (err) {
		console.warn(`stepsForRetainedHunks: rebuild failed for ${prefix}`, err)
		return stepsJson
	}
}

// One diff range in the merged doc attributed to a single hunk.
export interface HunkRange {
	proposalId: string
	hunkId: string
	range: ProposalDiffRange
}

// Per-hunk version of `computeProposalContributions`: attributes every diff
// range in the (discard-aware) merged doc back to the hunk that produced it via
// leave-one-out merges. Hunk ids always come from each proposal's ORIGINAL steps
// so they stay stable as the reviewer discards hunks. Already-discarded hunks are
// absent from the merged doc, so they get no range. Cost is O(retained hunks)
// merges; callers memoize on (currentHtml, proposals, discardedHunkIds).
export function computeHunkContributions(
	currentHtml: string,
	proposals: MergeProposalInput[],
	discardedHunkIds: ReadonlySet<string>,
): HunkRange[] {
	if (proposals.length === 0) return []

	const schema = getProposalEditorSchema()
	const effective = proposals.map((p) => ({
		...p,
		stepsJson: stepsForRetainedHunks(
			p.id,
			p.baseHtml,
			p.stepsJson,
			discardedHunkIds,
		),
	}))
	const fullHtml = mergeProposalsForArticle(currentHtml, effective).resultHtml
	const fullDoc = htmlToDoc(fullHtml, schema)
	const docSize = fullDoc.content.size
	const clamp = (pos: number) => Math.max(0, Math.min(pos, docSize))

	const out: HunkRange[] = []
	for (const proposal of proposals) {
		const hunks = computeProposalHunks(
			proposal.id,
			proposal.baseHtml,
			proposal.stepsJson,
		)
		for (const hunk of hunks) {
			if (discardedHunkIds.has(hunk.id)) continue
			const withHunkDiscarded = new Set(discardedHunkIds)
			withHunkDiscarded.add(hunk.id)
			const without = proposals.map((p) => ({
				...p,
				stepsJson: stepsForRetainedHunks(
					p.id,
					p.baseHtml,
					p.stepsJson,
					p.id === proposal.id ? withHunkDiscarded : discardedHunkIds,
				),
			}))
			const withoutDoc = htmlToDoc(
				mergeProposalsForArticle(currentHtml, without).resultHtml,
				schema,
			)
			let changes: Change[]
			try {
				changes = diffDocs(withoutDoc, fullDoc)
			} catch {
				continue
			}
			for (const change of changes) {
				const range = changeToDiffRange(change, withoutDoc, clamp)
				if (range) {
					out.push({ proposalId: proposal.id, hunkId: hunk.id, range })
				}
			}
		}
	}
	return out
}

interface RebaseWithMappings {
	appliedHtml: string
	conflicts: ProposalStepConflict[]
	proposedToResult: Mapping
	currentToResult: Mapping
}

// Like `rebaseProposal`, but also surfaces the prosemirror `Mapping`s that relate
// this proposal's proposed doc and the running doc to the rebased result. Those
// mappings let `computeReviewMerge` attribute each hunk to the final merged doc
// from the single merge pass, so no leave-one-out remerge runs.
function rebaseProposalWithMappings(args: {
	baseHtml: string
	currentHtml: string
	stepsJson: unknown[]
	schema: Schema
}): RebaseWithMappings {
	const { baseHtml, currentHtml, stepsJson, schema } = args

	if (baseHtml === currentHtml) {
		const transform = new Transform(htmlToDoc(baseHtml, schema))
		for (const json of stepsJson) {
			const step = Step.fromJSON(schema, json as Record<string, unknown>)
			const result = transform.maybeStep(step)
			if (result.failed) {
				throw new Error(`applyProposalSteps: step failed (${result.failed})`)
			}
		}
		return {
			appliedHtml: docToHtml(transform.doc),
			conflicts: [],
			proposedToResult: new Mapping(),
			currentToResult: transform.mapping,
		}
	}

	const baseDoc = htmlToDoc(baseHtml, schema)
	const { interveningSteps } = resolveInterveningSteps({
		baseDoc,
		currentHtml,
		chainSteps: null,
		schema,
	})
	const result = rebaseInternal({
		baseDoc,
		interveningSteps,
		stepsJson,
		schema,
		resolver: contentAnchoredResolver,
	})
	return {
		appliedHtml: result.resultHtml ?? currentHtml,
		conflicts: result.conflicts,
		proposedToResult: result.proposedToResult ?? new Mapping(),
		currentToResult: result.currentToResult ?? new Mapping(),
	}
}

// HUNK_DIFF_OPTS diff of one proposal in isolation (base -> base + all steps).
// Same shape `computeProposalHunks` derives ids from, but keeps the raw spans
// (fromA/toA/fromB/toB) the discard arithmetic needs. [] on parse/diff failure.
function proposalChanges(
	baseHtml: string,
	stepsJson: unknown[],
	schema: Schema,
): Change[] {
	let proposedDoc: Node
	try {
		proposedDoc = htmlToDoc(
			applyProposalSteps(baseHtml, stepsJson).html,
			schema,
		)
	} catch {
		return []
	}
	try {
		return diffDocs(htmlToDoc(baseHtml, schema), proposedDoc, HUNK_DIFF_OPTS)
	} catch {
		return []
	}
}

function mappedHunkRange(
	kind: Change["type"],
	mapPos: (pos: number, assoc: number) => number,
	effFrom: number,
	effTo: number,
	oldText: string,
	clamp: (pos: number) => number,
): ProposalDiffRange | null {
	if (kind === "insert") {
		const from = clamp(mapPos(effFrom, 1))
		const to = clamp(mapPos(effTo, -1))
		return to > from ? { kind: "insert", from, to } : null
	}
	if (kind === "delete") {
		return oldText.trim().length > 0
			? { kind: "delete", pos: clamp(mapPos(effFrom, -1)), oldText }
			: null
	}
	const from = clamp(mapPos(effFrom, 1))
	const to = clamp(mapPos(effTo, -1))
	return {
		kind: "replace",
		from,
		to,
		oldText: oldText.trim().length > 0 ? oldText : "",
	}
}

export interface ReviewMergeLimits {
	maxHtmlLength: number
	maxTotalHunks: number
	timeBudgetMs: number
	now?: () => number
}

export interface ReviewMergeResult {
	resultHtml: string
	outcomes: MergeProposalOutcome[]
	// Per-hunk diff ranges in the merged doc's coordinate space, or null when a
	// guardrail tripped. Null tells the review view to render the merged content
	// with the legacy base diff (or undecorated) instead of freezing.
	hunkRanges: HunkRange[] | null
	hunkCountByProposal: Map<string, number>
	degraded: "none" | "no-hunks"
}

const DEFAULT_REVIEW_MERGE_LIMITS = {
	maxHtmlLength: 200_000,
	maxTotalHunks: 150,
	timeBudgetMs: 3_000,
} as const

// Single-pass replacement for `mergeProposalsForArticle` + per-hunk
// `computeHunkContributions` on the review screen. Runs the discard-aware merge
// once, collects each proposal's rebase mappings, and maps every retained hunk's
// effective-proposed-doc position forward through those mappings to place its
// range in the final merged doc. Eliminates the O(hunks x proposals) leave-one-out
// remerges. Guardrails force `hunkRanges: null` on very large or slow articles so
// content always renders.
export function computeReviewMerge(
	currentHtml: string,
	proposals: MergeProposalInput[],
	discardedHunkIds: ReadonlySet<string>,
	limits?: Partial<ReviewMergeLimits>,
): ReviewMergeResult {
	const cfg = { ...DEFAULT_REVIEW_MERGE_LIMITS, ...limits }
	const clock = cfg.now ?? (() => Date.now())
	const startedAt = clock()
	const overBudget = () => clock() - startedAt > cfg.timeBudgetMs
	const schema = getProposalEditorSchema()

	interface Info {
		id: string
		baseHtml: string
		baseDoc: Node
		changes: Change[]
		effectiveSteps: unknown[]
	}
	const infos: Info[] = []
	const hunkCountByProposal = new Map<string, number>()
	let totalHunks = 0
	for (const proposal of proposals) {
		const changes = proposalChanges(
			proposal.baseHtml,
			proposal.stepsJson,
			schema,
		)
		hunkCountByProposal.set(proposal.id, changes.length)
		totalHunks += changes.length
		infos.push({
			id: proposal.id,
			baseHtml: proposal.baseHtml,
			baseDoc: htmlToDoc(proposal.baseHtml, schema),
			changes,
			effectiveSteps: stepsForRetainedHunks(
				proposal.id,
				proposal.baseHtml,
				proposal.stepsJson,
				discardedHunkIds,
			),
		})
	}

	const identity: RebaseWithMappings = {
		appliedHtml: currentHtml,
		conflicts: [],
		proposedToResult: new Mapping(),
		currentToResult: new Mapping(),
	}
	let runningHtml = currentHtml
	const outcomes: MergeProposalOutcome[] = []
	const mappings: { proposedToResult: Mapping; currentToResult: Mapping }[] = []
	for (const info of infos) {
		if (info.effectiveSteps.length === 0) {
			outcomes.push({ id: info.id, applied: true, conflicts: [] })
			mappings.push(identity)
			continue
		}
		try {
			const rebased = rebaseProposalWithMappings({
				baseHtml: info.baseHtml,
				currentHtml: runningHtml,
				stepsJson: info.effectiveSteps,
				schema,
			})
			runningHtml = rebased.appliedHtml
			outcomes.push({
				id: info.id,
				applied: rebased.conflicts.length === 0,
				conflicts: rebased.conflicts,
			})
			mappings.push(rebased)
		} catch {
			outcomes.push({
				id: info.id,
				applied: false,
				conflicts: [
					{ stepIndex: -1, description: "could not parse proposal steps" },
				],
			})
			mappings.push(identity)
		}
	}
	const resultHtml = runningHtml

	const degrade = (): ReviewMergeResult => ({
		resultHtml,
		outcomes,
		hunkRanges: null,
		hunkCountByProposal,
		degraded: "no-hunks",
	})
	if (currentHtml.length > cfg.maxHtmlLength) return degrade()
	if (totalHunks > cfg.maxTotalHunks) return degrade()
	if (overBudget()) return degrade()

	const finalDoc = htmlToDoc(resultHtml, schema)
	const docSize = finalDoc.content.size
	const clamp = (pos: number) => Math.max(0, Math.min(pos, docSize))

	const hunkRanges: HunkRange[] = []
	for (let i = 0; i < infos.length; i++) {
		if (overBudget()) return degrade()
		const info = infos[i]!
		const prefix = `${info.id}#`
		let anyDiscarded = false
		for (const id of discardedHunkIds) {
			if (id.startsWith(prefix)) {
				anyDiscarded = true
				break
			}
		}
		const forward: Mapping[] = [mappings[i]!.proposedToResult]
		for (let j = i + 1; j < infos.length; j++) {
			forward.push(mappings[j]!.currentToResult)
		}
		const mapPos = (pos: number, assoc: number) => {
			let p = pos
			for (const mapping of forward) p = mapping.map(p, assoc)
			return p
		}

		let delta = 0
		for (let index = 0; index < info.changes.length; index++) {
			const change = info.changes[index]!
			const spans = changeSpans(change)
			const discarded = discardedHunkIds.has(`${prefix}${index}`)
			const effFrom = anyDiscarded ? spans.fromA + delta : spans.fromB
			const effTo = anyDiscarded
				? effFrom + (spans.toB - spans.fromB)
				: spans.toB
			if (!discarded) {
				const oldText =
					change.type === "insert"
						? ""
						: info.baseDoc.textBetween(spans.fromA, spans.toA, " ", " ")
				const range = mappedHunkRange(
					change.type,
					mapPos,
					effFrom,
					effTo,
					oldText,
					clamp,
				)
				if (range) {
					hunkRanges.push({
						proposalId: info.id,
						hunkId: `${prefix}${index}`,
						range,
					})
				}
				delta += spans.toB - spans.fromB - (spans.toA - spans.fromA)
			}
		}
	}

	return {
		resultHtml,
		outcomes,
		hunkRanges,
		hunkCountByProposal,
		degraded: "none",
	}
}

/**
 * Second-phase async repair (AI). Walks the sync `RebaseResult`'s conflicts
 * and asks the resolver to repair each one. Successfully-repaired steps are
 * appended to `rebasedSteps` and the conflict is dropped. Always returns a
 * fresh result — never mutates the input.
 *
 * Wire in behind a feature flag on server-side accept paths only. The UI
 * preview must stay sync, so it should never call this.
 */
export async function repairConflicts(
	result: RebaseResult,
	asyncResolver: AsyncConflictResolver,
	ctx: { baseHtml: string; currentHtml: string },
): Promise<RebaseResult> {
	if (result.conflicts.length === 0) return result

	const schema = getProposalEditorSchema()
	const baseDoc = htmlToDoc(ctx.baseHtml, schema)

	// Replay rebasedSteps onto a doc so the resolver sees the running state.
	const transform = new Transform(htmlToDoc(ctx.currentHtml, schema))
	for (const json of result.rebasedSteps) {
		const step = parseStep(schema, json)
		if (step) safeMaybeStep(transform, step)
	}

	const repairedSteps: unknown[] = [...result.rebasedSteps]
	const unresolved: ProposalStepConflict[] = []

	for (const conflict of result.conflicts) {
		const originalStep = parseStep(schema, conflict.originalStepJson)
		if (!originalStep) {
			unresolved.push(conflict)
			continue
		}
		const repaired = await asyncResolver.resolve({
			step: originalStep,
			baseDoc,
			currentDoc: transform.doc,
			schema,
		})
		if (!repaired || safeMaybeStep(transform, repaired).failed) {
			unresolved.push(conflict)
			continue
		}
		repairedSteps.push(repaired.toJSON())
	}

	return {
		appliedHtml: docToHtml(transform.doc),
		rebasedSteps: repairedSteps,
		conflicts: unresolved,
		usedChain: result.usedChain,
	}
}

function parseStep(schema: Schema, json: unknown): Step | null {
	if (json == null) return null
	try {
		return Step.fromJSON(schema, json as Record<string, unknown>)
	} catch {
		return null
	}
}
